import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";

import type { PartyShellAccess } from "../types/api";
import type { PartyShellPalette } from "../theme/theme";

export function PartyTopBar({
  title,
  onBack,
  right,
}: {
  title: React.ReactNode;
  onBack: () => void;
  right?: React.ReactNode;
}) {
  const p = (useTheme().palette as unknown as { party: PartyShellPalette }).party;
  const { t } = useTranslation();

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ py: 1.25, px: 2, pb: 2 }}>
      <IconButton
        type="button"
        onClick={onBack}
        aria-label={t("party.chrome.backAria")}
        sx={{
          width: 44,
          height: 44,
          border: `1.5px solid ${p.inkFaint}`,
          color: p.ink,
          bgcolor: "transparent",
        }}
      >
        <Box component="svg" width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden sx={{ display: "block" }}>
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </IconButton>
      <Typography
        className="party-display"
        sx={{
          flex: 1,
          textAlign: "center",
          fontSize: 18,
          letterSpacing: "-0.01em",
          color: p.ink,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>{right}</Box>
    </Stack>
  );
}

export function PartyCoinBadge({
  amount,
  onClick,
  ariaLabel,
}: {
  amount: number | null;
  /** When set, the badge is a focusable button (e.g. open purchase flow). */
  onClick?: () => void;
  /** Accessibility label when `onClick` is set. */
  ariaLabel?: string;
}) {
  const p = (useTheme().palette as unknown as { party: PartyShellPalette }).party;
  const label = amount == null ? "—" : amount;
  const inner = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        display: "inline-flex",
        py: 0.75,
        pl: 1,
        pr: 1.5,
        borderRadius: 999,
        bgcolor: `color-mix(in oklab, ${p.gold} 18%, transparent)`,
        color: p.gold,
        fontFamily: "var(--font-display)",
        fontWeight: 800,
        fontSize: 14,
      }}
    >
      <Box component="svg" width={20} height={20} viewBox="0 0 24 24" aria-hidden sx={{ display: "block", flexShrink: 0 }}>
        <circle cx="12" cy="12" r="9" fill={p.gold} />
        <circle cx="12" cy="12" r="6" fill="none" stroke="#0E0B1A" strokeWidth="1.5" opacity={0.35} />
        <text
          x="12"
          y="15.5"
          textAnchor="middle"
          fontFamily="var(--font-display)"
          fontWeight="900"
          fontSize="10"
          fill="#0E0B1A"
        >
          ₡
        </text>
      </Box>
      <Typography component="span" sx={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, color: p.gold }}>
        {label}
      </Typography>
    </Stack>
  );

  if (onClick) {
    return (
      <ButtonBase
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? "Credits"}
        focusRipple
        sx={{
          borderRadius: 999,
          transition: "transform 0.12s ease, background-color 0.15s ease",
          "&:hover": {
            bgcolor: `color-mix(in oklab, ${p.gold} 28%, transparent)`,
          },
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        {inner}
      </ButtonBase>
    );
  }

  return inner;
}

export function PartyAccessBadge({
  access,
  price,
}: {
  access: PartyShellAccess;
  price: number | null;
}) {
  const { t } = useTranslation();

  const chipSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1,
    py: 0.5,
    borderRadius: "var(--r-chip, 14px)",
    fontSize: 13,
    fontWeight: 700,
  };

  if (access === "free") {
    return (
      <Typography component="span" className="party-chip party-chip-free" sx={chipSx}>
        {t("party.chrome.accessFree")}
      </Typography>
    );
  }
  if (access === "owned") {
    return (
      <Typography component="span" className="party-chip party-chip-owned" sx={chipSx}>
        {t("party.chrome.accessOwned")}
      </Typography>
    );
  }
  if (access === "paid") {
    return (
      <Typography component="span" className="party-chip party-chip-paid" sx={{ ...chipSx, gap: 0.5 }}>
        <Box component="svg" width={12} height={12} viewBox="0 0 24 24" aria-hidden sx={{ display: "block" }}>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity={0.3} />
          <text x="12" y="16" textAnchor="middle" fontWeight="900" fontSize="14" fill="currentColor">
            ₡
          </text>
        </Box>
        {t("party.chrome.accessPaid", { price: price ?? "—" })}
      </Typography>
    );
  }
  return (
    <Typography component="span" className="party-chip party-chip-locked" sx={chipSx}>
      {t("party.chrome.accessLocked")}
    </Typography>
  );
}

export function PartyTimer({ seconds, total }: { seconds: number; total: number }) {
  const p = (useTheme().palette as unknown as { party: PartyShellPalette }).party;
  const r = 52;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? seconds / total : 0;
  const stroke = seconds < 10 ? p.danger : p.hot;
  return (
    <Box sx={{ position: "relative", width: 132, height: 132 }}>
      <Box
        component="svg"
        viewBox="0 0 120 120"
        width={132}
        height={132}
        aria-hidden
        sx={{ display: "block", transform: "rotate(-90deg)" }}
      >
        <circle cx="60" cy="60" r={r} fill="none" stroke={p.inkFaint} strokeWidth="8" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 1s linear, stroke .3s" }}
        />
      </Box>
      <Typography
        className="party-display"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 44,
          color: seconds < 10 ? p.danger : p.ink,
        }}
      >
        {seconds}
      </Typography>
    </Box>
  );
}
